import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from "react-router-dom";
import { IzobrazevanjeTipFormType } from "../../../types/IzobrazevanjeTip/IzobrazevanjeTipFormType";
import { IzobrazevanjeTipListType } from "../../../types/IzobrazevanjeTip/IzobrazevanjeTipListType";
import { useContext } from "react";
import { FeedbackContext, Status } from "../../../context/FeedbackContext";
import getApiExceptionObjectFromAxiosError from "../../../utils/ProcessException";
import client from "../../../services/client";

// GET ALL
const useFetchIzobrazevanjeTipAll = () => {
    return useQuery<IzobrazevanjeTipListType[], AxiosError>(["izobrazevanjaTipi"], async () => {
        try {
            return (await client.get(`izobrazevanje_tip/all`)).data;
        }
        catch (error) {
            return Promise.reject(error);
        }
    });
};

// GET BY ID
const useFetchIzobrazevanjeTip = (izobrazevanjeTipId: number) => {
    return useQuery<IzobrazevanjeTipFormType, AxiosError>(["izobrazevanjaTipi", izobrazevanjeTipId], async () => {
        try {
            return (await client.get(`izobrazevanje_tip/${izobrazevanjeTipId}`)).data;
        }
        catch (error) {
            return Promise.reject(error);
        }
    });
};

// PUT-UPDATE
const useUpdateIzobrazevanjeTip = () => {
    const { displayFeedback } = useContext(FeedbackContext);
    const nav = useNavigate();
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError, IzobrazevanjeTipFormType>(async (izobrazevanjaTip) => {
        try {
            return (await client.put(`izobrazevanje_tip/${izobrazevanjaTip.izobrazevanjeTipId}`, izobrazevanjaTip));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(["izobrazevanjaTipi"]);
            nav("/sifrant/izobrazevanjeTip");
            if (displayFeedback)
                displayFeedback("Tip izobraževanja je bil uspešno urejen.", Status.SUCCESS)
        },
        onError: (err) => {
            let errMsg = "Pri posodabljanju tipa izobraževanja je prišlo do napake."
            let apiException = getApiExceptionObjectFromAxiosError(err);
            if (apiException?.Message)
                errMsg = apiException.Message;

            // TODO?: mogoče se lahko tu kaj poenoti za exception handling...
            if (displayFeedback)
                displayFeedback(errMsg, Status.ERROR)
        }
    });
};

// POST
const useAddIzobrazevanjeTip = () => {
    const { displayFeedback } = useContext(FeedbackContext);
    const nav = useNavigate();
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError, IzobrazevanjeTipFormType>(async (izobrazevanjaTip) => {
        try {
            return (await client.post(`izobrazevanje_tip`, izobrazevanjaTip));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(["izobrazevanjaTipi"]);
            nav("/sifrant/izobrazevanjeTip");
            if (displayFeedback)
                displayFeedback("Tip izobraževanja je bil uspešno dodan.", Status.SUCCESS)
        },
        onError: (err) => {
            let errMsg = "Pri ustvarjanju tipa izobraževanja je prišlo do napake."
            let apiException = getApiExceptionObjectFromAxiosError(err);
            if (apiException?.Message)
                errMsg = apiException.Message;

            // TODO?: mogoče se lahko tu kaj poenoti za exception handling...
            if (displayFeedback)
                displayFeedback(errMsg, Status.ERROR)
        }
    });
}

// ARCHIVE
const useArchiveIzobrazevanjeTip = () => {
    const { displayFeedback } = useContext(FeedbackContext);
    const nav = useNavigate();
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError, number>(async (izobrazevanjeTipId) => {
        try {
            return (await client.put(`izobrazevanje_tip/archive/${izobrazevanjeTipId}`, null));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(["izobrazevanjaTipi"]);
            nav("/sifrant/izobrazevanjeTip");
            if (displayFeedback)
                displayFeedback("Tip izobraževanja je bil uspešno arhiviran.", Status.SUCCESS)
        },
        onError: (err) => {
            let errMsg = "Pri arhiviranju tipa izobraževanja je prišlo do napake."
            let apiException = getApiExceptionObjectFromAxiosError(err);
            if (apiException?.Message)
                errMsg = apiException.Message;

            // TODO?: mogoče se lahko tu kaj poenoti za exception handling...
            if (displayFeedback)
                displayFeedback(errMsg, Status.ERROR)
        }
    });
}

// REACTIVATE
const useReactivateIzobrazevanjeTip = () => {
    const { displayFeedback } = useContext(FeedbackContext);
    const nav = useNavigate();
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError, number>(async (izobrazevanjeTipId) => {
        try {
            return (await client.put(`izobrazevanje_tip/reactivate/${izobrazevanjeTipId}`, null));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }, {
        onSuccess: (_, izobrazevanjeTipId) => {
            queryClient.invalidateQueries(["izobrazevanjaTipi"]);
            nav("/sifrant/izobrazevanjeTip");
            if (displayFeedback)
                displayFeedback("Tip izobraževanja je bil uspešno aktiviran.", Status.SUCCESS)
        },
        onError: (err) => {
            let errMsg = "Pri aktiviranju tipa izobraževanja je prišlo do napake."
            let apiException = getApiExceptionObjectFromAxiosError(err);
            if (apiException?.Message)
                errMsg = apiException.Message;

            // TODO?: mogoče se lahko tu kaj poenoti za exception handling...
            if (displayFeedback)
                displayFeedback(errMsg, Status.ERROR)
        }
    });
}

// DELETE
const useDeleteIzobrazevanjeTip = () => {
    const { displayFeedback } = useContext(FeedbackContext);
    const nav = useNavigate();
    const queryClient = useQueryClient();
    return useMutation<AxiosResponse, AxiosError, number>(async (izobrazevanjeTipId) => {
        try {
            return (await client.delete(`izobrazevanje_tip/${izobrazevanjeTipId}`));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }, {
        onSuccess: () => {
            queryClient.invalidateQueries(["izobrazevanjaTipi"]);
            nav("/sifrant/izobrazevanjeTip");
            if (displayFeedback)
                displayFeedback("Tip izobraževanja je bilo uspešno odstranjeno.", Status.SUCCESS)
        },
        onError: (err) => {
            let errMsg = "Pri brisanju tipa izobraževanja je prišlo do napake."
            let apiException = getApiExceptionObjectFromAxiosError(err);
            if (apiException?.Message)
                errMsg = apiException.Message;

            // TODO?: mogoče se lahko tu kaj poenoti za exception handling...
            if (displayFeedback)
                displayFeedback(errMsg, Status.ERROR)
        }
    });
};

export default useFetchIzobrazevanjeTipAll;
export { useFetchIzobrazevanjeTip, useUpdateIzobrazevanjeTip, useAddIzobrazevanjeTip, useArchiveIzobrazevanjeTip, useReactivateIzobrazevanjeTip, useDeleteIzobrazevanjeTip };